// src/data/claimPresentment.js
//
// PRESENTMENT - serving the claim on the carrier. Ruling 70, which closes ISS-38.
//
// Bob, 2026-08-29: "Anyone can. Commonly, the claimant herself with copies to the Organization
// representatives, just as common is the [LC], and of course the GC can and often does as well."
//
// ---------------------------------------------------------------------------------------------
// THE SHAPE OF THIS MODULE IS THE RULING, AND THE RULING IS MOSTLY A SUBTRACTION.
//
// 70.2 withdrew an authority model that was being scoped - who is PERMITTED to serve - and called it
// the wrong question. So THERE IS NO mayPresent(). There is no permission gate anywhere in this
// file, and its absence is deliberate and load-bearing. Compare src\data\claimReturn.js, whose
// FIRST export is mayReturn(): the return is an authority, presentment is an event. A reader who
// notices the asymmetry has noticed the ruling.
//
// "PRESENTMENT IS AN EVENT TO BE RECORDED, NOT AN AUTHORITY TO BE ENFORCED."
//
// What the platform owes instead is an accurate record of what happened, and refusing to record a
// thing that really happened because the actor was not an officer would be the platform deciding
// who may serve - which is precisely what 70 forbids.
// ---------------------------------------------------------------------------------------------

// WHO. Free among the three, and the fourth exists because 70.3 named it: a presentment may have
// been performed by somebody who is not on this list and was not looking at the platform.
export const PRESENTERS = [
  { key: 'claimant',      label: 'The claimant' },
  { key: 'local_chair',   label: 'The Local Chairperson' },
  { key: 'general_chair', label: 'The General Chairperson' },
  { key: 'other',         label: 'Someone else (name them)' },
];

// HOW. 70.2: "the method, because Article 20(c) service disputes turn on it." The four NRAB cases
// on the docket turn on exactly this - "Carrier denial not served in writing or by email as
// Article 20(c) requires." A method recorded as a guess is worse than one recorded as unknown, so
// `unknown` is a first-class value here and is never defaulted to.
export const METHODS = [
  { key: 'in_writing',   label: 'In writing, delivered or mailed' },
  // Ruling 314 (Bob 2026-10-14): timeliness is the day the carrier RECEIVED it, proven by how it was delivered.
  { key: 'email',        label: 'By e-mail, with proof of delivery' },
  { key: 'certified',    label: 'By USPS certified mail' },
  { key: 'hand',         label: 'By hand, signed for and dated' },
  { key: 'courier',      label: 'Same-day courier (special carrier, Uber, bike), with proof of delivery' },
  { key: 'portal',       label: "Through the carrier's own system" },
  { key: 'other',        label: 'Another way (say which)' },
  { key: 'unknown',      label: 'NOT KNOWN - the record does not say how' },
];

export const PRESENTER_KEYS = PRESENTERS.map(p => p.key);
const SIGNER_WORDS = { claimant: 'the claimant', local_chair: 'the Local Chairperson', general_chair: 'the General Chairperson' };
export const METHOD_KEYS = METHODS.map(m => m.key);

export function presenterLabel(k) {
  const hit = PRESENTERS.find(p => p.key === k);
  return hit ? hit.label : 'Not recorded';
}
export function methodLabel(k) {
  const hit = METHODS.find(m => m.key === k);
  return hit ? hit.label : 'Not recorded';
}

// COPIES TO. Bob names this explicitly for the claimant case - "with copies to the Organization
// representatives" - and 70.2 gives the reason in one line: "Where the member serves, the office
// must be copied, or the office does not know its own claim was filed."
//
// SO IT IS WARNED ON, NOT ENFORCED. A presentment by the claimant with nobody copied is a real
// event that really happens, and refusing to record it would lose the very filing the office most
// needs to know about. The warning is returned as data, exactly as returnWarning() is, so the
// surface can show it before the record is written and the officer can proceed anyway.
export function copiesWarning(input) {
  const i = input || {};
  const copies = Array.isArray(i.copiesTo) ? i.copiesTo.filter(x => String(x || '').trim()) : [];
  if (i.presentedBy === 'claimant' && copies.length === 0) {
    return {
      level: 'uncopied',
      text: 'The claimant served this himself and nobody in the organization is recorded as copied. '
          + 'If no representative was copied, the office does not know its own claim was filed - and '
          + 'the first time it finds out may be when the window has run.',
    };
  }
  return { level: 'ok', text: '' };
}

// ---------------------------------------------------------------------------------------------
// RULING 269 (2026-10-02): CONSEQUENCES ARE STATED BEFORE SUBMIT, POLITELY.
//
// Submitting a claim to the carrier IS recording its service here (ruling 70). Before that record is written,
// everything missing that carries a consequence is listed - one plain sentence each, saying what may follow. The
// person may still record it; the sentences shown and the choice are recorded with the service, the way ruling 260
// records the claimant's unsigned-claim warning (claimantDeclinedSignature: wording, version, who, when).
//
// This GENERALISES two warnings that already existed rather than adding a third mechanism:
//   - copiesWarning() above (ruling 70.2) - the uncopied claimant service - becomes one item, its text unchanged;
//   - ruling 260's unsigned-claim warning (src\components\ClaimFormActions.js UNSIGNED_WARNING) - carried here for
//     the person SUBMITTING, in ruling 269's own example sentence.
// PD-0: every sentence names its support in `source`. A missing item with no consequence the record supports is
// NOT listed (e.g. the filing window: "Nothing on this platform decides whether a filing was on time", section 364).
// Withdrawable by number: RULING_269_CONSEQUENCES false -> submitConsequences() returns [] and nothing is recorded.
export const RULING_269_CONSEQUENCES = true;
export const CONSEQUENCE_WORDING_VERSION = '269-v1';

export function submitConsequences(claim, input) {
  if (!RULING_269_CONSEQUENCES) return [];
  const c = claim || {};
  const i = input || {};
  const out = [];
  const d = c.claimantDeclinedSignature;
  if (d && d.choice === 'declined') {
    out.push({ key: 'claimant_signature',
      text: 'Without the claimant\u2019s signature, this claim may be challenged as unauthorized.',
      source: 'Ruling 269 (its own example); ruling 260 - the unsigned-claim warning the claimant acknowledged: '
            + '"The Carrier may argue the claim was not made or authorized by you".' });
  }
  if (!String(c.presentedTo || '').trim()) {
    out.push({ key: 'recipient',
      text: 'Without the name of the carrier officer it is presented to, the record cannot say who received it.',
      source: 'Ruling 177 (src\\data\\appealLadder.js, the presented act); ruling 262 (who the claim is presented to).' });
  }
  if (String(i.method || '').trim() === 'unknown') {
    out.push({ key: 'method',
      text: 'The method of service is recorded as not known. Article 20(c) service disputes turn on the method, so a '
          + 'dispute over service may follow.',
      source: 'Ruling 70.2 - "the method, because Article 20(c) service disputes turn on it".' });
  }
  const w = copiesWarning({ presentedBy: i.presentedBy, copiesTo: i.copiesTo });
  if (w.level !== 'ok') out.push({ key: 'copies', text: w.text, source: 'Ruling 70.2 - "Where the member serves, the office must be copied".' });
  // Ruling 314 (Bob 2026-10-14): the day the carrier received it is the day that counts, and the proof is what shows it.
  if (String(i.method || '').trim() && String(i.method || '').trim() !== 'unknown' && !String(i.proof || '').trim()) {
    out.push({ key: 'proof',
      text: 'No proof of receipt is recorded - who signed for it and when, the e-mail delivery confirmation, or the '
          + 'certified-mail receipt. Without it, the day the carrier received the claim can be disputed.',
      source: 'Ruling 314 - timeliness is the day of receipt, shown by its proof.' });
  }
  return out;
}

// The record of what was shown and what was chosen - ruling 260's shape (wording, version, who, when).
// null when nothing was shown: the absence of a warning is not recorded as a warning.
export function consequenceRecord(items, user, at) {
  const list = Array.isArray(items) ? items.filter(x => x && x.key && x.text) : [];
  if (!RULING_269_CONSEQUENCES || !list.length) return null;
  return {
    wordingVersion: CONSEQUENCE_WORDING_VERSION,
    shown: list.map(x => ({ key: x.key, text: x.text })),
    choice: 'recorded_anyway',
    acknowledgedAt: at || new Date().toISOString(),
    uid: (user && user.uid) || '',
    name: (user && (user.name || user.email)) || '',
  };
}

// A presentment date in the future is not a record of something that happened. It is either a typo
// or a plan, and neither belongs in the field that filing windows are argued from.
function isFutureISO(iso, nowMs) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ''))) return false;
  const d = new Date(String(iso) + 'T00:00:00');
  const today = new Date(typeof nowMs === 'number' ? nowMs : Date.now());
  today.setHours(0, 0, 0, 0);
  return d.getTime() > today.getTime();
}

// EVERYTHING THE RECORD WRITES, as a pure function.
// Returns { ok, errors[], warning, patch } - never a partial patch.
//
// WHAT IS DELIBERATELY ABSENT FROM THE PATCH:
//   status        presentment does not move a claim along the ladder. Serving the carrier and
//                 advancing the internal stage are two different facts and conflating them would
//                 make the ladder lie about where the matter is.
//   expiresAt     the filing window is anchored to the date of OCCURRENCE, not to service. Nothing
//                 in this module may touch it - the same guarantee ruling 72 makes.
export function presentmentPatch(claim, user, input) {
  const i = input || {};
  const errors = [];

  const presentedBy = String(i.presentedBy || '').trim();
  if (!presentedBy) {
    errors.push('Say who served it. Anyone may - the claimant, the Local Chairperson or the General '
              + 'Chairperson - but the record has to name one.');   // Record 589 (audit), ruling 168: a claimant reads this; ruling 70's number is not printed in it
  } else if (PRESENTER_KEYS.indexOf(presentedBy) === -1) {
    errors.push('"' + presentedBy + '" is not one of the recorded presenters.');
  }

  const name = String(i.presentedByName || '').trim();
  if (presentedBy === 'other' && !name) {
    errors.push('"Someone else" has to be a someone. Name who served it.');
  }

  const date = String(i.presentedOn || '').trim();
  if (!date) {
    errors.push('The date the carrier received it is required. It is the fact every time-limit argument turns on.');
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    errors.push('The date of service must be a real date (YYYY-MM-DD).');
  } else if (isFutureISO(date, i.nowMs)) {
    errors.push('That date is in the future. This field records a service that HAS happened, not one '
              + 'that is planned.');
  }

  const method = String(i.method || '').trim();
  if (!method) {
    errors.push('Say how it was served. Article 20(c) service disputes turn on the method, and it is '
              + 'never inferred here - if the record does not say, choose "NOT KNOWN".');
  } else if (METHOD_KEYS.indexOf(method) === -1) {
    errors.push('"' + method + '" is not one of the recorded methods.');
  }

  const copiesTo = (Array.isArray(i.copiesTo) ? i.copiesTo : [])
    .map(x => String(x || '').trim()).filter(Boolean);

  // Bob 2026-10-12: the one who submits signs - the claimant representing himself, the Local Chairperson, or the General
  // Chairperson. The form's signer (input.signer, from claimFormModel) must be the office recorded as serving it; "someone
  // else" (the General Chairperson's written assignment, ruling 305.13) is not checked here.
  const sg = i.signer && typeof i.signer === 'object' ? i.signer : null;
  if (sg && ['claimant', 'local_chair', 'general_chair'].indexOf(presentedBy) !== -1 && String(sg.by || '') !== presentedBy) {
    errors.push('The one who submits the claim signs it. The form is prepared for signature by '
      + (SIGNER_WORDS[sg.by] || 'someone else') + ', but you say ' + (SIGNER_WORDS[presentedBy] || presentedBy)
      + ' served it. Set "Prepared for submission by" to match, then record the service.');
  }

  const warning = copiesWarning({ presentedBy, copiesTo });

  if (errors.length) return { ok: false, errors, warning, patch: null };

  const patch = {
    presentedOn: date,
    presentedBy,
    presentedByName: name || null,          // null, never the empty string, and never a guess
    presentmentMethod: method,
    presentmentMethodOther: method === 'other' ? (String(i.methodOther || '').trim() || null) : null,
    copiesTo,
    // 70.3 - a presentment may be RECORDED by somebody other than the person who performed it, and
    // the two must never be confused. This is the recorder; presentedBy is the server.
    presentmentRecordedBy: (user && (user.name || user.email)) || 'Unknown',
    presentmentRecordedByRole: (user && user.role) || 'unknown',
    presentmentRecordedAt: i.at || new Date().toISOString(),
    presentmentNote: String(i.note || '').trim() || null,
    // Ruling 314: what shows the day the carrier received it - signed for by whom and when, a delivery confirmation,
    // a certified-mail receipt number. Recorded as given; null when none was given (the warning above says what that costs).
    presentmentProof: String(i.proof || '').trim().slice(0, 500) || null,
  };
  // Ruling 269: what was shown before this record was written, and the choice to record it anyway. Written only
  // when something was shown (input.consequencesShown, from submitConsequences on the same input).
  const shownRec = consequenceRecord(i.consequencesShown, user, patch.presentmentRecordedAt);
  if (shownRec) patch.presentmentWarnings = shownRec;
  // The signature block as it printed on the copy served - fixed the FIRST time service is recorded, never rewritten.
  if (sg && !(claim && claim.presentmentSigner)) {
    const t = (x) => String(x == null ? '' : x).trim().slice(0, 300);
    patch.presentmentSigner = { by: t(sg.by), name: t(sg.name), title: t(sg.title), contact: t(sg.contact),
      address: (Array.isArray(sg.address) ? sg.address : []).map(t).filter(Boolean).slice(0, 4),
      claimantSigns: sg.claimantSigns !== false, fixedAt: patch.presentmentRecordedAt };
  }

  return { ok: true, errors: [], warning, patch };
}

export function isPresented(claim) {
  return !!claim && !!claim.presentedOn;
}

// WHAT THE RECORD SAYS, for a surface to render. An unrecorded presentment reads as UNRECORDED and
// is never rendered as "not served" - the platform has no way to know the difference, and a claim
// served last week by a member who has not logged in looks identical to one never served at all.
export function presentmentSummary(claim) {
  if (!isPresented(claim)) {
    return {
      recorded: false,
      text: 'No presentment is recorded on this claim. That is NOT the same as saying it was never '
          + 'served - anyone may serve it, including the claimant, and a service performed away from '
          + 'the platform leaves no trace here until somebody records it.',   // Record 589 (audit), ruling 168: rendered to the member; ruling 70.3's number is not printed in it
    };
  }
  const who = claim.presentedBy === 'other'
    ? (claim.presentedByName || 'Someone else')
    : presenterLabel(claim.presentedBy);
  const how = claim.presentmentMethod === 'other'
    ? (claim.presentmentMethodOther || 'Another way')
    : methodLabel(claim.presentmentMethod);
  const copies = Array.isArray(claim.copiesTo) ? claim.copiesTo.filter(Boolean) : [];
  return {
    recorded: true,
    who,
    how,
    on: claim.presentedOn,
    copies,
    unknownMethod: claim.presentmentMethod === 'unknown',
    text: who + ' served it on ' + claim.presentedOn + ' - ' + how
        + (copies.length ? '. Copied to ' + copies.join(', ') : '. Nobody recorded as copied'),
  };
}
